import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  Paper,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Staff, CreateStaffData, UpdateStaffData } from '../../services/staffService';

interface StaffFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateStaffData | UpdateStaffData) => Promise<void>;
  staff?: Staff | null;
  loading?: boolean;
}

const DEPARTMENTS = [
  'Hair Styling',
  'Makeup',
  'Skincare',
  'Nail Care',
  'Spa & Wellness',
  'Reception',
  'Management',
  'Cleaning',
];

const POSITIONS = [
  'Hair Stylist',
  'Makeup Artist',
  'Skincare Specialist',
  'Nail Technician',
  'Spa Therapist',
  'Receptionist',
  'Manager',
  'Assistant Manager',
  'Trainee',
  'Senior Stylist',
];

const SKILLS = [
  'Hair Cutting',
  'Hair Coloring',
  'Hair Styling',
  'Bridal Makeup',
  'Party Makeup',
  'Facial Treatment',
  'Skincare Consultation',
  'Manicure',
  'Pedicure',
  'Nail Art',
  'Massage Therapy',
  'Waxing',
  'Threading',
  'Customer Service',
  'Product Knowledge',
];

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const StaffForm: React.FC<StaffFormProps> = ({
  open,
  onClose,
  onSubmit,
  staff,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateStaffData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    hireDate: new Date().toISOString().split('T')[0],
    position: '',
    department: '',
    skills: [],
    certifications: [],
    workingHours: {},
    hourlyRate: 0,
    commissionRate: 0,
    role: 'STAFF',
    password: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (staff) {
      setFormData({
        firstName: staff.firstName,
        lastName: staff.lastName,
        email: staff.email,
        phone: staff.phone,
        dateOfBirth: staff.dateOfBirth || '',
        hireDate: staff.hireDate.split('T')[0],
        position: staff.position,
        department: staff.department,
        skills: staff.skills,
        certifications: staff.certifications,
        workingHours: staff.workingHours,
        hourlyRate: staff.hourlyRate || 0,
        commissionRate: staff.commissionRate || 0,
        role: staff.user.role as 'STAFF' | 'MANAGER' | 'RECEPTIONIST',
        password: '', // Don't populate password for editing
      });
    } else {
      // Reset form for new staff
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        hireDate: new Date().toISOString().split('T')[0],
        position: '',
        department: '',
        skills: [],
        certifications: [],
        workingHours: {},
        hourlyRate: 0,
        commissionRate: 0,
        role: 'STAFF',
        password: '',
      });
    }
    setErrors({});
  }, [staff, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.position.trim()) newErrors.position = 'Position is required';
    if (!formData.department.trim()) newErrors.department = 'Department is required';
    if (!staff && !formData.password.trim()) newErrors.password = 'Password is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (staff) {
        // Update existing staff (exclude password)
        const { password, ...updateData } = formData;
        await onSubmit(updateData);
      } else {
        // Create new staff
        await onSubmit(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error submitting staff form:', error);
    }
  };

  const handleWorkingHoursChange = (day: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value,
        },
      },
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
            color: 'white',
            fontWeight: 800,
            fontSize: '1.5rem',
            textAlign: 'center',
            py: 3,
          }}
        >
          {staff ? 'Edit Staff Member' : 'Add New Staff Member'}
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Date of Birth"
                      value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                      onChange={(date) => setFormData(prev => ({ 
                        ...prev, 
                        dateOfBirth: date ? date.format('YYYY-MM-DD') : '' 
                      }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: '#E8B4B8' },
                              '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Hire Date"
                      value={dayjs(formData.hireDate)}
                      onChange={(date) => setFormData(prev => ({ 
                        ...prev, 
                        hireDate: date ? date.format('YYYY-MM-DD') : '' 
                      }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: '#E8B4B8' },
                              '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Job Information */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Job Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.department}>
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={formData.department}
                        onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                        label="Department"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        {DEPARTMENTS.map((dept) => (
                          <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.position}>
                      <InputLabel>Position</InputLabel>
                      <Select
                        value={formData.position}
                        onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                        label="Position"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        {POSITIONS.map((pos) => (
                          <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={formData.role}
                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
                        label="Role"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                        }}
                      >
                        <MenuItem value="STAFF">Staff</MenuItem>
                        <MenuItem value="MANAGER">Manager</MenuItem>
                        <MenuItem value="RECEPTIONIST">Receptionist</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      multiple
                      options={SKILLS}
                      value={formData.skills}
                      onChange={(_, newValue) => setFormData(prev => ({ ...prev, skills: newValue }))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={option}
                            sx={{ borderColor: '#E8B4B8', color: '#E8B4B8' }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Skills"
                          placeholder="Select skills"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': { borderColor: '#E8B4B8' },
                              '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Hourly Rate (₹)"
                      type="number"
                      value={formData.hourlyRate}
                      onChange={(e) => setFormData(prev => ({ ...prev, hourlyRate: Number(e.target.value) }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Commission Rate (%)"
                      type="number"
                      value={formData.commissionRate}
                      onChange={(e) => setFormData(prev => ({ ...prev, commissionRate: Number(e.target.value) }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: '#E8B4B8' },
                          '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Security */}
            {!staff && (
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'rgba(232, 180, 184, 0.05)',
                    borderRadius: 3,
                    border: '1px solid rgba(232, 180, 184, 0.1)',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                    Security
                  </Typography>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    error={!!errors.password}
                    helperText={errors.password}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': { borderColor: '#E8B4B8' },
                        '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                      },
                    }}
                  />
                </Paper>
              </Grid>
            )}

            {/* Working Hours */}
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                  Working Hours
                </Typography>
                {DAYS_OF_WEEK.map((day) => (
                  <Box key={day.key} sx={{ mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.workingHours[day.key]?.isWorking || false}
                              onChange={(e) => handleWorkingHoursChange(day.key, 'isWorking', e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#E8B4B8',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#E8B4B8',
                                },
                              }}
                            />
                          }
                          label={day.label}
                        />
                      </Grid>
                      {formData.workingHours[day.key]?.isWorking && (
                        <>
                          <Grid item xs={12} sm={4}>
                            <TimePicker
                              label="Start Time"
                              value={formData.workingHours[day.key]?.start ? dayjs(`2000-01-01T${formData.workingHours[day.key].start}`) : null}
                              onChange={(time) => {
                                if (time) {
                                  const timeStr = time.format('HH:mm');
                                  handleWorkingHoursChange(day.key, 'start', timeStr);
                                }
                              }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      '&:hover fieldset': { borderColor: '#E8B4B8' },
                                      '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                                    },
                                  },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TimePicker
                              label="End Time"
                              value={formData.workingHours[day.key]?.end ? dayjs(`2000-01-01T${formData.workingHours[day.key].end}`) : null}
                              onChange={(time) => {
                                if (time) {
                                  const timeStr = time.format('HH:mm');
                                  handleWorkingHoursChange(day.key, 'end', timeStr);
                                }
                              }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  sx: {
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      '&:hover fieldset': { borderColor: '#E8B4B8' },
                                      '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                                    },
                                  },
                                },
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={onClose}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
              color: '#666',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              bgcolor: '#E8B4B8',
              '&:hover': {
                bgcolor: '#D4A4A8',
              },
            }}
          >
            {loading ? 'Saving...' : staff ? 'Update Staff' : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default StaffForm;